/* eslint-disable no-param-reassign */
import { createSelector } from 'reselect'
import {
    identity,
    NOT_AVAILABLE,
    EMPTY_IMMUTABLE_MAP,
    EMPTY_ARRAY,
    SLASH
} from '@lacele/const'

const DEFAULT_STORE_TARGET_PROP = 'storeTarget'

function warnNoStoreTarget() {
    if (console) {
        (console.warn || console.log)('storeTarget not set!')
    }
    return NOT_AVAILABLE
}

export const targetToKeyPath = (storeTarget, rootKeyPath) => {
    if (!storeTarget) {
        storeTarget = warnNoStoreTarget()
    }
    return rootKeyPath.concat(storeTarget.split(SLASH))
}

// *** internal use only ***

const keyPathToState = (state, storeKeyPath, defaultState = EMPTY_IMMUTABLE_MAP) => (
    state.getIn(storeKeyPath) || defaultState
)

const createGetStoreKeyPath = (storeTargetProp = DEFAULT_STORE_TARGET_PROP) => createSelector(
    (_, props) => props[storeTargetProp],
    (_, props) => (props.dynamicReducer ? props.dynamicReducer.getRootKeyPath() : EMPTY_ARRAY),
    targetToKeyPath
)

export function toGlobalSelectors(localSelectors, globalSelectors) {
    if (localSelectors) {
        Object.keys(localSelectors).forEach((propKey) => {
            if (propKey !== 'default') {
                const propValue = localSelectors[propKey]
                if (typeof propValue === 'function') {
                    globalSelectors[propKey] = (state, props) => {
                        const localState = globalSelectors.getState(state, props)
                        return propValue(localState, props)
                    }
                } else {
                    globalSelectors[propKey] = propValue
                }
            }
        })
    }
    return globalSelectors
}

// ********************************************************

export const createChildSelector = (selectorCreator) => createSelector(
    identity,
    (_, props) => props,
    selectorCreator,
    (state, props, selector) => selector(state, props)
)

export const createGetState = (defaultState, storeTargetProp) => createSelector(
    identity,
    createGetStoreKeyPath(storeTargetProp),
    () => defaultState,
    keyPathToState
)

export const createReduxProp = (propName) => (_, props) => props[propName]

/**
 * Selector factory to build a storeTarget.
 *
 * @param {Function} getTargetFunc function that return a storeTarget calculated
 *                                 from an input storeTarget
 * @param {string} storeTargetProp optional name of the storeTarget property in props
 */
export const createReduxTargetSelector
    = (getTargetFunc, storeTargetProp = DEFAULT_STORE_TARGET_PROP) => createSelector(
        createReduxProp(storeTargetProp),
        getTargetFunc
    )

// *****************************

export const createGlobalSelectors = (rootKeyPath, target, localSelectors) => {
    const keyPath = targetToKeyPath(target, rootKeyPath)
    const getState = createSelector(identity, () => keyPath, keyPathToState)
    return toGlobalSelectors(localSelectors, {
        getState,
        target,
        keyPath
    })
}
